import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ShieldX,
  History,
  Sparkles,
  Loader2,
  ArrowRight,
  LogOut,
  Crown,
  CreditCard,
  ExternalLink,
  Receipt,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import {
  fetchCheckHistory,
  getCheckoutStatus,
  createCheckoutSession,
  createBillingPortalSession,
  fetchReceipts,
  downloadReceiptPdf,
  syncSubscription,
} from "../lib/api";
import { usePricing } from "../lib/usePricing";
import FamilyProfiles from "../components/account/FamilyProfiles";
import { listRoutines } from "../lib/api";
import "../styles/customer-workspace.css";
import { cn } from "../lib/utils";
import TreatmentTracker from "../components/account/TreatmentTracker";
import PrescriptionRecheck from "../components/account/PrescriptionRecheck";

// Checks are currently unlimited for every plan — the backend quota gate is a
// no-op during early access (see _gate_quota_or_raise). Flip to false if a real
// quota is ever re-enabled so the "X of Q used · Y remaining" copy returns.
const CHECKS_UNLIMITED = true;

const TONE = {
  RED: { bg: "bg-avoid-bg", border: "border-avoid-border", text: "text-avoid-deep", Icon: ShieldX, label: "AVOID" },
  YELLOW: { bg: "bg-caution-bg", border: "border-caution-border", text: "text-caution-deep", Icon: AlertTriangle, label: "CAUTION" },
  GREEN: { bg: "bg-safe-bg", border: "border-safe-border", text: "text-safe-deep", Icon: CheckCircle2, label: "No label conflict found" },
  UNKNOWN: { bg: "bg-cream-100", border: "border-surface-line", text: "text-ink-mute", Icon: Sparkles, label: "Not yet reviewed" },
};

export default function Account() {
 const {user,ready}=useAuth();
 const navigate=useNavigate();
 useEffect(()=>{if(ready&&!user)navigate('/');},[ready,user,navigate]);
 if(!ready||!user)return <div className="family-loading">Loading your account…</div>;
 return <div className="family-workspace"><FamilyProfiles>{profile=><AccountContent key={profile.id} profile={profile}/>}</FamilyProfiles></div>;
}
function AccountContent({profile}) {
  const { user, ready, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [history, setHistory] = useState([]);
  const [tab,setTab]=useState('overview');
  const [routines,setRoutines]=useState([]);
  const [routineError,setRoutineError]=useState(false);
  useEffect(()=>{let live=true;listRoutines().then(d=>{if(live)setRoutines(d.items||[]);}).catch(()=>{if(live)setRoutineError(true);});return()=>{live=false;};},[]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [managing, setManaging] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const autoSyncedRef = useRef(false);
  const pricing = usePricing();

  const handleSync = async ({ silent = false } = {}) => {
    setSyncing(true);
    try {
      const res = await syncSubscription();
      if (res?.is_pro) {
        if (refresh) await refresh();
        toast.success("You're on Pro", {
          description: "Your subscription is now active on this account.",
        });
      } else if (!silent) {
        toast.message("No active subscription found", {
          description: "If you just paid, give it a moment and try again, or email hello@blendsafe.com.",
        });
      }
      return res;
    } catch {
      if (!silent) {
        toast.error("Couldn't sync from Stripe", { description: "Please try again in a moment." });
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const { url } = await createCheckoutSession("pro_monthly");
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Checkout failed";
      toast.error("Couldn't open checkout", {
        description: typeof msg === "string" ? msg : "Please try again.",
      });
      setUpgrading(false);
    }
  };

  const handleManage = async () => {
    setManaging(true);
    try {
      const { url } = await createBillingPortalSession();
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No portal URL");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("Couldn't open billing portal", {
        description:
          typeof msg === "string"
            ? msg
            : "Please try again in a moment.",
      });
      setManaging(false);
    }
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate("/");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCheckHistory();
        if (!cancelled) setHistory(data);
      } catch {
        toast.error("Couldn't load your history", {
          description: "Check your connection and try again — your past checks are still saved.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
      try {
        const rs = await fetchReceipts();
        if (!cancelled) setReceipts(rs);
      } catch {
        /* receipts are best-effort; ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, ready, navigate]);

  // Handle Stripe success redirect — poll payment status until paid.
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || !user) return;
    let attempts = 0;
    let cancelled = false;
    setPaymentStatus({ state: "checking" });

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.payment_status === "paid") {
          setPaymentStatus({ state: "paid" });
          if (refresh) await refresh();
          toast.success("Welcome to Pro \u2014 you're all set", {
            description: "Your routine checks, discussion reports, and safer swaps are unlocked.",
          });
          // Clean the session_id from the URL after success
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (res.status === "expired") {
          setPaymentStatus({ state: "expired" });
          toast.error("Checkout session expired", {
            description: "No worries \u2014 nothing was charged. Tap \u201CUpgrade to Pro\u201D again when you're ready.",
          });
          return;
        }
        attempts += 1;
        if (attempts >= 8) {
          // Webhook/poll didn't confirm in time — reconcile straight from Stripe.
          const r = await handleSync({ silent: true });
          setPaymentStatus({ state: r?.is_pro ? "paid" : "timeout" });
          if (r?.is_pro) {
            params.delete("session_id");
            setParams(params, { replace: true });
          }
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= 8) {
          const r = await handleSync({ silent: true });
          setPaymentStatus({ state: r?.is_pro ? "paid" : "error" });
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("session_id"), user]);

  // Self-heal a missed webhook: if a signed-in user isn't Pro yet, reconcile
  // from Stripe once on load (silent — only toasts if it flips them to Pro).
  useEffect(() => {
    if (!ready || !user || user.is_pro || autoSyncedRef.current) return;
    autoSyncedRef.current = true;
    handleSync({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  if (!ready) {
    return (
      <main className="bg-cream-50 min-h-[calc(100vh-68px)] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-soft" />
      </main>
    );
  }
  if (!user) return null;

  const checksLeft = Math.max(0, user.quota - user.checks_used);
  const plan = user.plan || (user.is_pro ? "pro" : "free");
  const isFounding = plan === "founding";
  const isPaid = plan === "pro" || plan === "founding";
  const renewalDate = user.pro_until
    ? new Date(user.pro_until).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const planLabel = isFounding ? "Founding" : plan === "pro" ? "Pro" : "Free";

  return (
    <main className={`family-account family-tab-${tab}`}>
      <section className="max-w-screen-lg mx-auto px-6 sm:px-10 pt-10 pb-16">
        {/* Page header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div className="min-w-0">
            <h1
              data-testid="account-page-title"
              className="text-3xl sm:text-4xl font-extrabold tracking-[-0.02em] text-ink"
            >
              {profile.name === "My profile" ? "Your skincare workspace" : `${profile.name}’s skincare workspace`}
            </h1>
            <p data-testid="account-email" className="text-[14px] text-ink-soft mt-1.5 break-words">
              Products, routines and medication checks for {profile.name === "My profile" ? "you" : profile.name}.
            </p>
          </div>
          <button
            data-testid="account-sign-out"
            onClick={() => {
              signOut();
              navigate("/");
            }}
            className="inline-flex items-center gap-1.5 text-[13px] text-ink-soft hover:text-ink transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>

        <div className="family-primary"><button onClick={()=>navigate('/check')}>Check a product <ArrowRight size={18}/></button><span>Checking for {profile.name}</span><button className="family-plan-short" onClick={()=>setTab('billing')}>{planLabel}{isPaid?' · Active':''} · Manage plan</button></div>
        <nav className="family-tabs" aria-label="Workspace sections">{[['overview','Overview'],['routines','Saved routines'],['history','Check history'],['billing','Account & billing']].map(([id,label])=><button key={id} aria-current={tab===id?'page':undefined} onClick={()=>setTab(id)}>{label}</button>)}</nav>
        {/* Payment status banner (only when returning from Stripe) */}
        {paymentStatus?.state === "checking" && (
          <div
            data-testid="payment-status-checking"
            className="mb-6 rounded-2xl border border-surface-line bg-white p-4 flex items-center gap-3"
          >
            <Loader2 className="w-4 h-4 animate-spin text-ink-soft" />
            <span className="text-[14px] text-ink-soft">
              Confirming your payment with Stripe…
            </span>
          </div>
        )}
        {paymentStatus?.state === "paid" && (
          <div
            data-testid="payment-status-paid"
            className="mb-6 rounded-2xl border border-safe-border bg-safe-bg p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-safe-deep" />
            <div>
              <div className="font-bold text-safe-deep">Welcome to Pro!</div>
              <div className="text-[13px] text-safe-deep/80">
                Pro is active on your account.
              </div>
            </div>
          </div>
        )}
        {paymentStatus?.state === "expired" && (
          <div className="mb-6 rounded-2xl border border-avoid-border bg-avoid-bg p-4 text-avoid-deep text-[14px]">
            Your checkout session expired. You can try again from the upgrade
            modal.
          </div>
        )}

        <div className="family-billing-panel" hidden={tab!=="billing"}><p className="family-account-email">{user.email} · Account-wide billing and usage</p>
        {/* Plan + quota cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div
            data-testid="account-plan-card"
            className="rounded-2xl border border-surface-line bg-white p-6 shadow-subtle"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
              Current plan
            </div>
            <div className="mt-2 flex items-center gap-2">
              {isPaid ? (
                <>
                  <Crown className="w-5 h-5 text-peach-500" />
                  <span
                    data-testid="account-plan-label"
                    className="text-2xl font-extrabold tracking-tight text-ink"
                  >
                    {planLabel}
                  </span>
                  <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-safe-bg text-safe-deep border border-safe-border font-bold uppercase tracking-wider">
                    Active
                  </span>
                </>
              ) : (
                <>
                  <span
                    data-testid="account-plan-label"
                    className="text-2xl font-extrabold tracking-tight text-ink"
                  >
                    Free
                  </span>
                  <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-cream-100 text-ink-mute border border-surface-line font-bold uppercase tracking-wider">
                    {CHECKS_UNLIMITED ? "Unlimited" : `${checksLeft} left`}
                  </span>
                </>
              )}
            </div>

            {isPaid && renewalDate && (
              <p
                data-testid="account-renewal-date"
                className="mt-2 text-[13px] text-ink-soft"
              >
                {isFounding
                  ? `Founding access — free, renews ${renewalDate}`
                  : `Renews on ${renewalDate}`}
              </p>
            )}

            {isPaid ? (
              <Button
                variant="outline"
                data-testid="account-manage-subscription"
                onClick={handleManage}
                disabled={managing}
                className="mt-5 w-full h-11 rounded-xl border-surface-line text-[14px] font-semibold"
              >
                {managing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Opening…
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage subscription
                    <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                data-testid="account-upgrade-cta"
                onClick={handleUpgrade}
                disabled={upgrading}
                className="mt-5 w-full h-11 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold"
              >
                {upgrading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Opening checkout…
                  </>
                ) : (
                  <>
                    Upgrade to Pro · {pricing.formatted}/{pricing.interval}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}

            {!isPaid && (
              <button
                type="button"
                data-testid="account-sync-stripe"
                onClick={() => handleSync()}
                disabled={syncing}
                className="mt-3 w-full text-[12.5px] text-ink-mute hover:text-ink underline underline-offset-2 disabled:opacity-60"
              >
                {syncing ? "Checking Stripe…" : "Already paid? Sync from Stripe"}
              </button>
            )}
          </div>

          <div
            data-testid="account-usage-card"
            className="rounded-2xl border border-surface-line bg-white p-6 shadow-subtle"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
              Checks used
            </div>
            <div className="mt-2 text-2xl font-extrabold tracking-tight text-ink tabular-nums">
              {user.checks_used}
            </div>
            <p data-testid="account-checks-period" className="mt-1 text-[12px] text-ink-mute">
              {(CHECKS_UNLIMITED || (isPaid && (!user.quota || user.quota >= 9999)))
                ? "Unlimited this cycle"
                : `${user.checks_used} of ${user.quota} used this cycle · ${checksLeft} remaining`}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/check")}
              data-testid="account-run-check"
              className="mt-5 w-full h-11 rounded-xl border-surface-line text-[14px] font-semibold"
            >
              Run a new check
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        </div>
        <div className="family-overview-grid" hidden={tab!=="overview"&&tab!=="routines"}>
          <section className="family-routines"><div className="family-section-heading"><h2>{profile.name === 'My profile' ? 'Your saved routines' : `${profile.name}’s saved routines`}</h2><button onClick={()=>navigate('/routines')}>View all routines →</button></div>
          {routineError?<p role="alert">Unable to load routines. <button onClick={()=>window.location.reload()}>Retry</button></p>:routines.length?routines.slice(0,tab==='overview'?1:routines.length).map(r=><a className="family-routine" key={r.id} href={`/routine?reopen=${encodeURIComponent(r.id)}`}><span className="family-routine-symbol" aria-hidden="true">◫</span><span><strong>{r.label||'Saved routine'}</strong><small>{r.items?.length||0} products</small></span><span>Open routine →</span></a>):<div className="family-empty"><p>Your saved routines will appear here.</p><button onClick={()=>navigate('/routine')}>Build your first routine →</button></div>}
          </section>
          <div className="family-tracker" hidden={tab!=='overview'}><TreatmentTracker /></div>
        </div>
        <div hidden={tab!=='overview'}>
        {/* Treatment tracker + prescription re-check */}
        <PrescriptionRecheck isPro={isPaid} onUpgrade={handleUpgrade} />

        </div>
        <div hidden={tab!=="billing"}>
        {/* Billing — receipts */}
        {receipts.length > 0 && (
          <div className="mb-10" data-testid="account-billing">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-ink-soft" />
              <h2 className="text-lg font-bold text-ink tracking-tight">Billing</h2>
              <span className="ml-auto text-[12px] text-ink-mute">
                {receipts.length} receipt{receipts.length > 1 ? "s" : ""}
              </span>
            </div>
            <ul className="space-y-2.5">
              {receipts.map((r) => (
                <li
                  key={r.receiptNo}
                  data-testid={`receipt-row-${r.receiptNo}`}
                  className="rounded-2xl border border-surface-line bg-white p-4 sm:p-5 flex items-center gap-3 sm:gap-4 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-bold text-ink">
                      {r.receiptNumber} · {r.total}
                    </div>
                    <div className="text-[13px] text-ink-soft">
                      {r.planName} · {r.receiptDate}
                      {r.isFounding ? " · Founding" : ""}
                    </div>
                  </div>
                  {r.sessionId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`receipt-view-${r.receiptNo}`}
                      onClick={() => navigate(`/account/receipt/${r.sessionId}`)}
                      className="text-[13px]"
                    >
                      View
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`receipt-pdf-${r.receiptNo}`}
                    onClick={() => downloadReceiptPdf(r.receiptNo).catch(() =>
                      toast.error("Couldn't download the PDF", { description: "Please try again." }))}
                    className="text-[13px] border-surface-line"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    PDF
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        </div>
        {/* History */}
        <div className="family-history" hidden={tab!=="overview"&&tab!=="history"}>
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-ink-soft" />
            <h2 className="text-lg font-bold text-ink tracking-tight">
              {tab==='history'?'Check history':'Recent checks'}
            </h2>
            <span className="ml-auto text-[12px] text-ink-mute">
              {history.length} saved {tab==='overview'&&<button className="family-view-all" onClick={()=>setTab('history')}>View all checks →</button>}
            </span>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-surface-line bg-white p-8 flex items-center justify-center gap-2 text-ink-mute">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          ) : history.length === 0 ? (
            <div
              data-testid="account-history-empty"
              className="rounded-2xl border border-dashed border-surface-line bg-cream-100/40 p-10 text-center"
            >
              <Sparkles className="w-6 h-6 text-peach-400 mx-auto" />
              <h3 className="mt-3 text-lg font-bold text-ink">
                No checks yet
              </h3>
              <p className="text-ink-soft text-[14px] mt-1.5">
                Your verdicts will live here as a tidy history.
              </p>
              <Button
                onClick={() => navigate("/check")}
                className="mt-5 h-11 px-5 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold"
              >
                Run your first check
              </Button>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {history.slice(0,tab==='overview'?3:history.length).map((h) => (
                <HistoryRow key={h.id} item={h} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}

function HistoryRow({ item }) {
  const tone = TONE[item.overall_risk] || TONE.UNKNOWN;
  const Icon = tone.Icon;
  const date = item.created_at
    ? new Date(item.created_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

  return (
    <li
      data-testid={`history-row-${item.id}`}
      className={cn(
        "rounded-2xl border bg-white p-4 sm:p-5 transition-all hover:-translate-y-0.5 hover:shadow-card",
        tone.border,
      )}
    >
      <div className="flex items-start gap-4">
        <span
          className={cn(
            "inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0",
            tone.bg,
            tone.text,
          )}
        >
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                "text-[10px] uppercase tracking-[0.22em] font-bold",
                tone.text,
              )}
            >
              {tone.label}
            </span>
            <span className="text-[11px] text-ink-mute">·</span>
            <span className="text-[12px] text-ink-mute">{date}</span>
          </div>
          <div className="mt-1 text-[15px] font-bold text-ink">
            {item.product_name}
          </div>
          <div className="text-[13px] text-ink-soft">
            {item.medication_name || item.medication_id}

          </div>
          <p className="family-history-caption">Saved check · Run again for a current result.</p>
          <a className="family-history-recheck" href={`/check?med=${encodeURIComponent(item.medication_id||'')}`}>Check again →</a>
          {item.matched_ingredients?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {item.matched_ingredients.slice(0, 4).map((ing) => (
                <span
                  key={ing}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-cream-100 border border-surface-line text-ink-soft"
                >
                  {ing}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
